import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Tooltip as ChartTooltip,
  Legend,
} from 'chart.js';
import { Bar, Doughnut, Line } from 'react-chartjs-2';
import {
  AlertTriangle,
  Banknote,
  Boxes,
  CreditCard,
  Percent,
  Receipt,
  RefreshCw,
  RotateCcw,
  TrendingUp,
} from 'lucide-react';
import { analytics2API } from '../../api';
import { useCurrency } from '../../contexts/CurrencyContext';
import { useAppStore } from '../../stores/useAppStore';
import DateRangeControl from '../Reports/DateRangeControl';
import { Button } from '../ui';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, ArcElement, ChartTooltip, Legend);

const PRESETS = ['today', 'yesterday', 'this_week', 'last_week', 'this_month', 'last_month', 'this_year', 'custom'];
const PAYMENT_LABEL = { cash: 'Cash', bank: 'Bank', split: 'Split', none: 'No payment' };
const PAYMENT_COLOR = { cash: '#16a34a', bank: '#2563eb', split: '#d97706', none: '#9ca3af' };

function KpiCard({ icon, label, value, valueTitle, sub, tone = 'gray' }) {
  const tones = {
    gray: 'bg-gray-100 text-gray-600',
    green: 'bg-green-100 text-green-700',
    blue: 'bg-blue-100 text-blue-700',
    amber: 'bg-amber-100 text-amber-700',
    red: 'bg-red-100 text-red-700',
  };
  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
      <div className="flex items-start justify-between">
        <div className="min-w-0">
          <p className="text-sm font-medium text-gray-600">{label}</p>
          <p className="text-2xl font-bold text-gray-900 mt-1 whitespace-nowrap" title={valueTitle}>{value}</p>
          {sub && <p className="text-xs text-gray-500 mt-1">{sub}</p>}
        </div>
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${tones[tone]}`}>
          {icon && React.createElement(icon, { className: 'w-5 h-5' })}
        </div>
      </div>
    </div>
  );
}

function Panel({ title, children, right }) {
  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
        {right}
      </div>
      {children}
    </div>
  );
}

const Analytics2Dashboard = () => {
  const { formatPrice, getCurrencySymbol } = useCurrency();
  const { addNotification } = useAppStore();
  const [range, setRange] = useState({ preset: 'this_month', start: null, end: null, field: null });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const money = useCallback((v) => formatPrice(Number(v || 0)), [formatPrice]);
  const int = (v) => Number(v || 0).toLocaleString();
  // Compact money for KPI cards so big figures fit (e.g. Rs 1.19M); exact value shown on hover.
  const compactFmt = useMemo(() => new Intl.NumberFormat('en', { notation: 'compact', maximumFractionDigits: 2 }), []);
  const sym = (getCurrencySymbol && getCurrencySymbol()) || 'Rs';
  const moneyK = useCallback((v) => `${sym}${compactFmt.format(Number(v || 0))}`, [sym, compactFmt]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { preset: range.preset };
      if (range.preset === 'custom' && range.start && range.end) {
        params.start = range.start;
        params.end = range.end;
      }
      const res = await analytics2API.getDashboard(params);
      setData(res.data);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Failed to load analytics';
      setError(typeof msg === 'string' ? msg : 'Failed to load analytics');
      addNotification({ type: 'error', message: typeof msg === 'string' ? msg : 'Failed to load analytics' });
    } finally {
      setLoading(false);
    }
  }, [range, addNotification]);

  useEffect(() => {
    load();
  }, [load]);

  const trend = useMemo(() => {
    const rows = data?.daily_trend || [];
    return {
      labels: rows.map((r) => r.date),
      datasets: [
        {
          label: 'Net sales',
          data: rows.map((r) => r.net),
          borderColor: '#2563eb',
          backgroundColor: 'rgba(37,99,235,0.12)',
          fill: true,
          tension: 0.3,
          yAxisID: 'y',
        },
        {
          label: 'Bills',
          data: rows.map((r) => r.bills),
          borderColor: '#16a34a',
          backgroundColor: '#16a34a',
          type: 'bar',
          yAxisID: 'y1',
          opacity: 0.4,
        },
      ],
    };
  }, [data]);

  const paymentChart = useMemo(() => {
    const rows = data?.payment_mix || [];
    return {
      labels: rows.map((r) => PAYMENT_LABEL[r.method] || r.method),
      datasets: [{ data: rows.map((r) => Math.max(r.net, 0)), backgroundColor: rows.map((r) => PAYMENT_COLOR[r.method] || '#9ca3af') }],
    };
  }, [data]);

  const topChart = useMemo(() => {
    const rows = data?.top_items || [];
    return {
      labels: rows.map((r) => (r.name || '').slice(0, 28)),
      datasets: [{ label: 'Net sold', data: rows.map((r) => r.net), backgroundColor: '#2563eb' }],
    };
  }, [data]);

  const k = data?.kpis;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Business overview</h1>
          <p className="text-sm text-gray-500">
            Sales, payments, stock and cash at a glance. Figures are after discount, on Pakistan time.
          </p>
        </div>
        <div className="flex items-end gap-3">
          <DateRangeControl value={range} onChange={(patch) => setRange((r) => ({ ...r, ...patch }))} presets={PRESETS} compact allowAll={false} applied={data?.period} />
          <Button variant="secondary" onClick={load} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>
      </div>

      {error && <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">{error}</div>}

      {loading && !data ? (
        <div className="min-h-[40vh] flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
        </div>
      ) : (
        k && (
          <>
            {/* KPI row */}
            <div className="grid gap-4 grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
              <KpiCard icon={TrendingUp} tone="green" label="Net sales" value={moneyK(k.net_sales)} valueTitle={money(k.net_sales)} sub={`${int(k.bills)} bills`} />
              <KpiCard icon={Banknote} tone="blue" label="Amount charged" value={moneyK(k.charged)} valueTitle={money(k.charged)} sub={`cash ${moneyK(k.cash)} · bank ${moneyK(k.bank)}`} />
              <KpiCard icon={Receipt} tone="gray" label="Items sold" value={int(k.items_sold)} sub={`avg bill ${money(k.avg_bill)}`} />
              <KpiCard icon={Percent} tone="amber" label="Discounts given" value={moneyK(k.discounts)} valueTitle={money(k.discounts)} sub={`${k.discount_pct}% of gross`} />
              <KpiCard icon={RotateCcw} tone="red" label="Refunds" value={moneyK(k.refunds)} valueTitle={money(k.refunds)} sub="returns against sales" />
              <KpiCard icon={RefreshCw} tone="gray" label="Exchanges" value={int(data.exchanges.count)} sub={`net ${moneyK(data.exchanges.difference)}`} />
            </div>

            {/* Charts */}
            <div className="grid gap-4 lg:grid-cols-3">
              <Panel title="Daily net sales">
                {trend.labels.length ? (
                  <div className="h-64">
                    <Line
                      data={trend}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        interaction: { mode: 'index', intersect: false },
                        plugins: { legend: { display: true, position: 'bottom' }, tooltip: { callbacks: { label: (c) => (c.dataset.label === 'Net sales' ? `Net: ${money(c.parsed.y)}` : `Bills: ${c.parsed.y}`) } } },
                        scales: { y: { position: 'left', ticks: { callback: (v) => money(v) } }, y1: { position: 'right', grid: { drawOnChartArea: false } } },
                      }}
                    />
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 py-10 text-center">No sales in this period.</p>
                )}
              </Panel>
              <Panel title="Payment mix">
                {paymentChart.labels.length ? (
                  <div className="h-64">
                    <Doughnut data={paymentChart} options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { position: 'bottom' }, tooltip: { callbacks: { label: (c) => `${c.label}: ${money(c.parsed)}` } } } }} />
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 py-10 text-center">No payments in this period.</p>
                )}
              </Panel>
              <Panel title="Top items by net sold">
                {topChart.labels.length ? (
                  <div className="h-64">
                    <Bar
                      data={topChart}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        indexAxis: 'y',
                        plugins: { legend: { display: false }, tooltip: { callbacks: { label: (c) => money(c.parsed.x) } } },
                        scales: { x: { ticks: { callback: (v) => money(v) } } },
                      }}
                    />
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 py-10 text-center">No items sold in this period.</p>
                )}
              </Panel>
            </div>

            {/* Secondary: cash + exchanges + inventory, plus damaged stock where the branch has damages */}
            <div className={`grid gap-4 ${data.damages ? 'lg:grid-cols-2 xl:grid-cols-4' : 'lg:grid-cols-3'}`}>
              <Panel title="Cash reconciliation (closings)">
                <dl className="space-y-2 text-sm">
                  <Row label="Days closed" value={int(data.closings.days)} />
                  <Row label="Cash sales" value={money(data.closings.cash_sales)} />
                  <Row label="Refunds" value={money(data.closings.refunds)} />
                  <Row label="Bank received" value={money(data.closings.bank_received)} />
                  <Row label="Cash short / over" value={money(data.closings.variance)} tone={data.closings.variance < 0 ? 'red' : 'green'} />
                </dl>
                {data.closings.variance < 0 && (
                  <p className="mt-2 flex items-center gap-1 text-xs text-red-600"><AlertTriangle className="w-3.5 h-3.5" /> Drawer is short over this period.</p>
                )}
              </Panel>
              <Panel title="Exchanges">
                <dl className="space-y-2 text-sm">
                  <Row label="Exchanges" value={int(data.exchanges.count)} />
                  <Row label="Value given out" value={money(data.exchanges.given)} />
                  <Row label="Value taken back" value={money(data.exchanges.returned)} />
                  <Row label="Net difference (+ customer paid)" value={money(data.exchanges.difference)} />
                  <Row label="Cash in / out" value={`${money(data.exchanges.cash_in)} / ${money(data.exchanges.cash_out)}`} />
                </dl>
              </Panel>
              <Panel title="Inventory (now, all stock)">
                <dl className="space-y-2 text-sm">
                  <Row label="Items in stock" value={int(data.inventory.items_in_stock)} />
                  <Row label="Units in stock" value={int(data.inventory.stock)} />
                  <Row label="Value at cost" value={money(data.inventory.stock_value)} />
                  <Row label="Value at sale price" value={money(data.inventory.retail_value)} />
                  <Row label="Dead stock (never sold)" value={`${int(data.inventory.dead_count)} items · ${money(data.inventory.dead_value)}`} tone="amber" />
                </dl>
              </Panel>
              {data.damages && (
                <Panel
                  title="Damaged stock"
                  right={<Link to="/damages" className="text-xs text-primary-600 hover:underline">open damages</Link>}
                >
                  <dl className="space-y-2 text-sm">
                    <Row label="Damaged in this period" value={`${int(data.damages.pieces)} pcs · ${money(data.damages.value)}`} />
                    {data.damages.by_reason.map((r) => (
                      <Row key={r.reason} label={<span className="pl-3 text-gray-500">{r.reason}</span>} value={`${int(r.pieces)} pcs · ${money(r.value)}`} />
                    ))}
                    <Row
                      label="Sent to warehouse"
                      value={`${int(data.damages.sent.shipments)} shipment${data.damages.sent.shipments === 1 ? '' : 's'} · ${int(data.damages.sent.pieces)} pcs`}
                    />
                    <Row
                      label="Waiting to be sent (now)"
                      value={`${int(data.damages.waiting.pieces)} pcs · ${money(data.damages.waiting.value)}`}
                      tone={data.damages.waiting.pieces > 0 ? 'amber' : undefined}
                    />
                  </dl>
                </Panel>
              )}
            </div>

            {/* Top items table */}
            <Panel title="Top items" right={<span className="text-xs text-gray-500">click a name for its stock history</span>}>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Item</th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Qty sold</th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Net sold</th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Stock now</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {data.top_items.length === 0 && (
                      <tr><td colSpan={4} className="px-4 py-6 text-center text-sm text-gray-500">No items sold in this period.</td></tr>
                    )}
                    {data.top_items.map((it) => (
                      <tr key={it.id} className="hover:bg-gray-50">
                        <td className="px-4 py-2 text-sm">
                          {it.id ? (
                            <Link to={`/items/${it.id}/history`} className="text-primary-600 hover:underline">{it.name}</Link>
                          ) : (
                            it.name
                          )}
                        </td>
                        <td className="px-4 py-2 text-sm text-right tabular-nums">{int(it.qty)}</td>
                        <td className="px-4 py-2 text-sm text-right tabular-nums">{money(it.net)}</td>
                        <td className="px-4 py-2 text-sm text-right tabular-nums">{int(it.stock)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Panel>

            {data.generated_at && (
              <p className="text-xs text-gray-400 text-right">
                Period {data.period?.start_local} to {data.period?.end_local} · generated {new Date(data.generated_at).toLocaleString()}
              </p>
            )}
          </>
        )
      )}
    </div>
  );
};

function Row({ label, value, tone }) {
  const toneCls = tone === 'red' ? 'text-red-600' : tone === 'green' ? 'text-green-600' : tone === 'amber' ? 'text-amber-700' : 'text-gray-900';
  return (
    <div className="flex items-center justify-between gap-3">
      <dt className="text-gray-600">{label}</dt>
      <dd className={`font-medium tabular-nums ${toneCls}`}>{value}</dd>
    </div>
  );
}

export default Analytics2Dashboard;
